import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { postAuthOtpRequest, postAuthOtpVerify } from '@/api/auth';
import { useAuth } from '@/stores/auth';
import { homeForRole } from '../roles';
import { AuthShell, FormError, errText, inputCls } from '../shared';

/** Confirms the phone number of a newly registered applicant (OTP purpose `verify`). */
export function VerifyPhonePage() {
  const user = useAuth((s) => s.user);
  const nav = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  if (!user) return <Navigate to="/login" replace />;
  if (user.phoneVerified || !user.phone) return <Navigate to={homeForRole(user.role)} replace />;
  const base = { school_code: user.schoolCode || undefined, identifier: user.phone, purpose: 'verify' };
  const home = () => nav(homeForRole(user.role), { replace: true });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await postAuthOtpVerify({ ...base, code });
      useAuth.getState().patchUser({ phoneVerified: true });
      home();
    } catch (err) { setError(errText(err, 'Verification failed.')); } finally { setBusy(false); }
  };
  const resend = async () => {
    setError(null); setInfo(null);
    try { await postAuthOtpRequest(base); setInfo('A new code has been sent.'); } catch (err) { setError(errText(err)); }
  };

  return (
    <AuthShell title="Verify your phone" subtitle={`Enter the 6-digit code we sent to ${user.phone}.`}>
      <form onSubmit={submit} className="space-y-3">
        <input className={`${inputCls} tracking-[0.5em] text-center text-lg`} inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="••••••" autoFocus aria-label="Verification code" />
        <FormError text={error} />
        {info && <p className="text-sm text-green-700">{info}</p>}
        <Button type="submit" className="w-full" disabled={busy || code.length !== 6}>{busy ? 'Verifying…' : 'Verify phone'}</Button>
        <Button type="button" variant="ghost" className="w-full" onClick={resend}>Resend code</Button>
        <Button type="button" variant="ghost" className="w-full" onClick={home}>Do this later</Button>
      </form>
    </AuthShell>
  );
}
